import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useAPI from '../hooks/useAPI';
import {
  getAnalyticalReports,
  downloadAnalyticalReport,
  getAnalyticalSuggestions,
  submitAnalyticalSuggestion,
  editAnalyticalSuggestion,
} from '../services/api';

// Per-flag colors for the report card.
//   bg / accent — legacy palette (kept for any other consumer)
//   headerBg / headerText / cardBorder / bodyTint — academic-card layout
//   (v1.10.2 redesign): coloured header strip + tinted body inside a
//   hairline border. Flag drives the colour family, sourced from the
//   Assignment-page chip palette philosophy (soft tint + accent).
const FLAG_STYLES = {
  red:    { bg: '#fde2e2', accent: '#d63031', label: 'Action needed',
            headerBg: '#d63031', headerText: '#fff',
            cardBorder: '#f5b7b1', bodyTint: '#fef5f5' },
  yellow: { bg: '#fff3cd', accent: '#fdcb6e', label: 'Informational',
            headerBg: '#d4a017', headerText: '#fff',
            cardBorder: '#f1d99c', bodyTint: '#fffbf2' },
  green:  { bg: '#d4edda', accent: '#00b894', label: 'Confirmed improvement',
            headerBg: '#15803d', headerText: '#fff',
            cardBorder: '#a8d5b8', bodyTint: '#f4faf6' },
  gray:   { bg: '#e8eef3', accent: '#95a5a6', label: 'Notes only',
            headerBg: '#475569', headerText: '#fff',
            cardBorder: '#cbd5e1', bodyTint: '#fafbfc' },
};

// v1.10.2 — Suggestion-status pills (academic palette, sparse colour).
// White background + 1px hairline border + small-caps label. Single accent
// per status, no fills. Lets the report cards stay the visual focus.
const SUGGESTION_STYLES = {
  open:      { border: '#64748b', color: '#475569', label: 'OPEN' },
  picked:    { border: '#4f46e5', color: '#4338ca', label: 'PICKED' },
  done:      { border: '#15803d', color: '#15803d', label: 'DONE' },
  dismissed: { border: '#cbd5e1', color: '#94a3b8', label: 'DISMISSED' },
};

const FLAG_EMOJI = { red: '🔴', yellow: '🟡', green: '🟢', gray: '⚪' };

const MAX_SUGGESTION_LEN = 2000;
const SUGGESTIONS_POLL_MS = 60_000;  // refetch suggestions every 60s so the
                                      // weekly-run status changes (open→picked
                                      // / picked→done) surface without reload.

function fmtKB(bytes) {
  if (!bytes) return '';
  return `${Math.round(bytes / 1024)} KB`;
}

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function fmtShortDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined,
      { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return iso; }
}

export default function AnalyticalReports() {
  const [weeksToLoad, setWeeksToLoad] = useState(4);
  const { data: reportsData, loading: loadingReports } =
    useAPI(() => getAnalyticalReports(weeksToLoad), [weeksToLoad]);
  const { data: suggData, loading: loadingSugg, reload: reloadSugg } =
    useAPI(() => getAnalyticalSuggestions(''));

  const weeks = reportsData?.weeks || [];
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [search, setSearch] = useState('');

  // v1.10.2 — refetch suggestions every 60s so weekly-run status updates
  // (open→picked, picked→done, etc.) appear without a manual reload.
  useEffect(() => {
    const id = setInterval(() => { reloadSugg(); }, SUGGESTIONS_POLL_MS);
    return () => clearInterval(id);
  }, [reloadSugg]);

  // Default to the most-recent week whenever the underlying list changes.
  useEffect(() => {
    if (weeks.length && !selectedWeek) setSelectedWeek(weeks[0].week_tag);
  }, [weeks, selectedWeek]);

  const currentWeek = useMemo(
    () => weeks.find(w => w.week_tag === selectedWeek) || weeks[0],
    [weeks, selectedWeek]
  );

  // v1.10.2 — search across title + summary + project (case-insensitive).
  // Empty query → all reports. Filter applies to the currently-selected week.
  const filteredReports = useMemo(() => {
    const all = currentWeek?.reports || [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(r => {
      const hay = [
        r.title || '', r.summary || '', r.project || '',
      ].join('  ').toLowerCase();
      return hay.includes(q);
    });
  }, [search, currentWeek]);

  return (
    <div>
      <h1 style={styles.title}>Analytical Reports</h1>
      <p style={styles.subtitle}>
        Weekly Changes Impact Analysis — click any card to download the full <code>.docx</code>.
      </p>

      {/* v1.10.2 — Suggestion section moved to TOP, collapsed by default. */}
      <SuggestionBlock
        suggestions={suggData?.suggestions || []}
        loading={loadingSugg}
        onChanged={reloadSugg}
      />

      {/* Week filter + search */}
      {weeks.length > 0 && (
        <div style={styles.weekBar}>
          <span style={styles.weekLabel}>Week</span>
          <select
            value={selectedWeek || ''}
            onChange={e => setSelectedWeek(e.target.value)}
            style={styles.weekSelect}
          >
            {weeks.map(w => (
              <option key={w.week_tag} value={w.week_tag}>
                {w.week_tag} — generated {fmtShortDate(w.generated_at)}
              </option>
            ))}
          </select>
          {weeks.length === weeksToLoad && (
            <button
              onClick={() => setWeeksToLoad(n => n + 4)}
              style={styles.loadMoreBtn}
            >
              + load older weeks
            </button>
          )}
          <div style={{ flex: 1 }} />
          <span style={styles.weekLabel}>Search</span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Filter by title / summary / project…"
            style={styles.searchInput}
          />
          {search && (
            <button onClick={() => setSearch('')} style={styles.clearBtn}>
              clear
            </button>
          )}
        </div>
      )}

      {loadingReports && !reportsData && (
        <div style={styles.loader}>Loading reports…</div>
      )}

      {!loadingReports && weeks.length === 0 && (
        <div style={styles.empty}>
          No reports yet. The Changes Impact Analysis weekly job hasn't published any.
        </div>
      )}

      {currentWeek && (
        <div style={styles.weekBlock}>
          <div style={styles.weekHeader}>
            {currentWeek.week_tag}
            <span style={styles.weekHeaderSub}>
              · Generated {fmtDate(currentWeek.generated_at)}
            </span>
            {search && (
              <span style={styles.weekHeaderSub}>
                · {filteredReports.length} of {currentWeek.reports.length} match "{search}"
              </span>
            )}
          </div>
          <div style={styles.reportList}>
            {filteredReports.length === 0 && search ? (
              <div style={styles.emptyInline}>
                No reports match "{search}" in this week.
              </div>
            ) : (
              filteredReports.map((r, i) => (
                <ReportCard
                  key={r.id}
                  report={r}
                  isLast={i === filteredReports.length - 1}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Legend */}
      <div style={styles.legend}>
        🔴 Action needed &nbsp; · &nbsp; 🟡 Informational &nbsp; · &nbsp;
        🟢 Confirmed improvement &nbsp; · &nbsp; ⚪ Notes only
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// v1.10.2 — Academic-card layout (per Yanir's APA-style reference image).
// Each card = its own panel: coloured header strip (flag-driven) + tinted
// body inside a hairline border. Replaces the previous flat single-table look.
function ReportCard({ report, isLast }) {
  const flagStyle = FLAG_STYLES[report.interesting_flag] || FLAG_STYLES.gray;
  const [downloading, setDownloading] = useState(false);

  const handleClick = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadAnalyticalReport(report.id);
    } catch (e) {
      console.error(e);
      alert('Download failed: ' + (e.message || e));
    } finally {
      setDownloading(false);
    }
  }, [report.id, downloading]);

  return (
    <div
      onClick={handleClick}
      style={{
        ...styles.reportCard,
        cursor: downloading ? 'wait' : 'pointer',
        border: `1px solid ${flagStyle.cardBorder}`,
        background: flagStyle.bodyTint,
      }}
    >
      <div style={{
        ...styles.reportHeader,
        background: flagStyle.headerBg,
        color: flagStyle.headerText,
      }}>
        <span style={{ fontSize: 16 }} title={flagStyle.label}>
          {FLAG_EMOJI[report.interesting_flag] || '⚪'}
        </span>
        <span style={styles.reportTitle}>
          REPORT {report.slot} — {report.title}
        </span>
      </div>
      <div style={styles.reportBodyInner}>
      <div style={styles.reportSummary}>{report.summary}</div>
      <div style={styles.reportFooter}>
        <span style={styles.downloadLink}>
          📄 {downloading ? 'Downloading…' : 'Download .docx'}
        </span>
        {report.file_size_bytes ? (
          <span style={styles.reportMeta}>({fmtKB(report.file_size_bytes)})</span>
        ) : null}
        {report.project ? (
          <span style={styles.reportMeta}>· {report.project}</span>
        ) : null}
        {report.test_date ? (
          <span style={styles.reportMeta}>· change {fmtShortDate(report.test_date)}</span>
        ) : null}
      </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// v1.10.2 — Top-of-page collapsible suggestion section. Default state is a
// single long button; click expands the form + grouped list.
function SuggestionBlock({ suggestions, loading, onChanged }) {
  const [expanded, setExpanded] = useState(false);

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        style={styles.suggestionToggleClosed}
      >
        + Add your needed report for next Sunday
        <span style={styles.suggestionToggleHint}>
          {suggestions.length ? ` (${suggestions.length} in queue)` : ''}
        </span>
      </button>
    );
  }

  return (
    <div style={styles.suggestionBlockOpen}>
      <button
        onClick={() => setExpanded(false)}
        style={styles.suggestionToggleOpen}
        title="Collapse"
      >
        − Add your needed report for next Sunday
      </button>
      <SuggestionForm onSubmitted={onChanged} />
      <SuggestionList
        suggestions={suggestions}
        loading={loading}
        onChanged={onChanged}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function SuggestionForm({ onSubmitted }) {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState('');

  const handleSubmit = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await submitAnalyticalSuggestion(trimmed);
      setText('');
      setToast('Saved — will be reviewed in next week\'s run.');
      onSubmitted();
      setTimeout(() => setToast(''), 4000);
    } catch (e) {
      console.error(e);
      setToast('Failed to submit. Please try again.');
      setTimeout(() => setToast(''), 4000);
    } finally {
      setSubmitting(false);
    }
  }, [text, submitting, onSubmitted]);

  return (
    <div>
      <textarea
        value={text}
        onChange={e => setText(e.target.value.slice(0, MAX_SUGGESTION_LEN))}
        placeholder='Free text — what should we investigate next week? e.g. "When was Madhopura&apos;s 75 °C threshold first set?"'
        rows={3}
        style={styles.suggestionTextarea}
      />
      <div style={styles.suggestionRow}>
        <span style={styles.charCount}>
          {text.length} / {MAX_SUGGESTION_LEN}
        </span>
        <div style={{ flex: 1 }} />
        {toast && <span style={styles.toast}>{toast}</span>}
        <button
          onClick={handleSubmit}
          disabled={!text.trim() || submitting}
          style={{
            ...styles.submitBtn,
            opacity: !text.trim() || submitting ? 0.5 : 1,
            cursor: !text.trim() || submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? 'Submitting…' : 'Submit suggestion'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// v1.10.2 — Grouped suggestion list per spec §4b.
//   OPEN (top), then PICKED (with W## badge), then DONE (clickable to download
//   the resulting report), then DISMISSED (collapsed under toggle).
function SuggestionList({ suggestions, loading, onChanged }) {
  const [showDismissed, setShowDismissed] = useState(false);

  const grouped = useMemo(() => ({
    open:      suggestions.filter(s => s.status === 'open'),
    picked:    suggestions.filter(s => s.status === 'picked'),
    done:      suggestions.filter(s => s.status === 'done'),
    dismissed: suggestions.filter(s => s.status === 'dismissed'),
  }), [suggestions]);

  if (loading && !suggestions.length) {
    return <div style={styles.loader}>Loading suggestions…</div>;
  }
  if (!loading && !suggestions.length) {
    return <div style={styles.suggestionEmpty}>No suggestions yet. Be the first.</div>;
  }

  return (
    <div style={styles.suggestionListWrap}>
      <SuggestionGroup
        label="OPEN"
        items={grouped.open}
        onChanged={onChanged}
      />
      <SuggestionGroup
        label="PICKED"
        items={grouped.picked}
        onChanged={onChanged}
      />
      <SuggestionGroup
        label="DONE"
        items={grouped.done}
        onChanged={onChanged}
      />
      {grouped.dismissed.length > 0 && (
        <div>
          <button
            onClick={() => setShowDismissed(v => !v)}
            style={styles.showDismissedBtn}
          >
            {showDismissed ? '▾' : '▸'} Dismissed ({grouped.dismissed.length})
          </button>
          {showDismissed && (
            <SuggestionGroup
              label={null}
              items={grouped.dismissed}
              onChanged={onChanged}
            />
          )}
        </div>
      )}
    </div>
  );
}

function SuggestionGroup({ label, items, onChanged }) {
  if (!items.length) return null;
  return (
    <div style={styles.suggestionGroup}>
      {label && (
        <div style={styles.suggestionGroupHeader}>
          {label} <span style={styles.suggestionGroupCount}>({items.length})</span>
        </div>
      )}
      {items.map(s => (
        <SuggestionItem key={s.id} suggestion={s} onSaved={onChanged} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function SuggestionItem({ suggestion: s, onSaved }) {
  const ss = SUGGESTION_STYLES[s.status] || SUGGESTION_STYLES.open;
  // v1.10.2 — PICKED gets the W## badge appended; DONE shows "→ report" hint.
  const pillLabel = s.status === 'picked' && s.picked_for_week
    ? `${ss.label} ${s.picked_for_week.replace('2026-', '')}`  // "PICKED W18"
    : ss.label;

  const editable = s.status === 'open';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(s.text);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const startEdit = (e) => {
    e.stopPropagation();
    setDraft(s.text);
    setErrMsg('');
    setEditing(true);
  };

  const cancelEdit = (e) => {
    e.stopPropagation();
    setEditing(false);
    setDraft(s.text);
    setErrMsg('');
  };

  const saveEdit = async (e) => {
    e.stopPropagation();
    const text = draft.trim();
    if (!text || saving) return;
    setSaving(true);
    setErrMsg('');
    try {
      await editAnalyticalSuggestion(s.id, text);
      setEditing(false);
      onSaved();
    } catch (e2) {
      console.error(e2);
      setErrMsg(e2.message || 'Edit failed');
    } finally {
      setSaving(false);
    }
  };

  const handleClick = () => {
    if (editing) return;
    if (s.status === 'done' && s.resulting_report_id) {
      downloadAnalyticalReport(s.resulting_report_id).catch(console.error);
    }
  };

  // Strikethrough for dismissed (academic visual cue, no pill colour change).
  const textStyle = s.status === 'dismissed'
    ? { ...styles.suggestionText, textDecoration: 'line-through', color: '#94a3b8' }
    : styles.suggestionText;

  return (
    <div
      style={{
        ...styles.suggestionItem,
        cursor: editing ? 'default' : (s.status === 'done' ? 'pointer' : 'default'),
      }}
      title={!editing ? (s.status_note || '') : ''}
      onClick={handleClick}
    >
      <span style={{
        ...styles.suggestionPill,
        background: '#fff',
        color: ss.color,
        borderColor: ss.border,
      }}>
        {pillLabel}
      </span>

      {editing ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value.slice(0, MAX_SUGGESTION_LEN))}
            rows={2}
            style={styles.editTextarea}
            onClick={e => e.stopPropagation()}
            autoFocus
          />
          {errMsg && <div style={styles.editError}>{errMsg}</div>}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={styles.charCount}>{draft.length} / {MAX_SUGGESTION_LEN}</span>
            <div style={{ flex: 1 }} />
            <button onClick={cancelEdit} style={styles.cancelBtn}>Cancel</button>
            <button
              onClick={saveEdit}
              disabled={saving || !draft.trim() || draft === s.text}
              style={{
                ...styles.saveBtn,
                opacity: (saving || !draft.trim() || draft === s.text) ? 0.5 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <span style={textStyle}>{s.text}</span>
          <span style={styles.suggestionMeta}>
            — {s.suggested_by}, {fmtShortDate(s.suggested_at)}
          </span>
          {s.status === 'done' && (
            <span style={styles.suggestionMeta} title="Click to download the resulting report">
              → report
            </span>
          )}
          {editable && (
            <button
              onClick={startEdit}
              style={styles.editBtn}
              title="Edit your suggestion"
            >
              ✎ edit
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
const styles = {
  title: { fontSize: 24, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 14, color: '#636e72', marginTop: 4, marginBottom: 16 },
  weekBar: {
    display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
    flexWrap: 'wrap',
  },
  weekLabel: { fontSize: 13, fontWeight: 600, color: '#636e72' },
  weekSelect: {
    padding: '6px 10px', borderRadius: 6, border: '1px solid #dfe6e9',
    fontSize: 13, fontFamily: 'inherit', background: '#fff',
  },
  searchInput: {
    padding: '6px 10px', borderRadius: 6, border: '1px solid #dfe6e9',
    fontSize: 13, fontFamily: 'inherit', background: '#fff',
    minWidth: 240,
  },
  clearBtn: {
    padding: '4px 10px', borderRadius: 6, border: '1px solid #dfe6e9',
    background: '#fff', cursor: 'pointer', fontSize: 12, color: '#636e72',
    fontFamily: 'inherit',
  },
  loadMoreBtn: {
    padding: '6px 12px', borderRadius: 6, border: '1px solid #dfe6e9',
    background: '#f5f6fa', cursor: 'pointer', fontSize: 12, color: '#636e72',
    fontFamily: 'inherit',
  },
  loader: { padding: 24, color: '#95a5a6', fontSize: 13 },
  empty: {
    padding: 32, color: '#95a5a6', fontSize: 13,
    background: '#fafbfc', borderRadius: 8, textAlign: 'center',
  },
  emptyInline: {
    padding: 24, color: '#95a5a6', fontSize: 13, textAlign: 'center',
    background: '#fafbfc',
  },
  weekBlock: { marginBottom: 24 },
  weekHeader: {
    fontSize: 14, fontWeight: 700, color: '#2d3436',
    marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #ecf0f1',
  },
  weekHeaderSub: { fontWeight: 400, color: '#95a5a6', marginLeft: 4 },
  // v1.10.2 — academic-card layout. List is just a vertical stack with a
  // gap between cards (no shared box). Each card is its own panel with a
  // coloured header strip, tinted body, and hairline border (set inline
  // from FLAG_STYLES so the colour family matches the report's flag).
  reportList: {
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  reportCard: {
    borderRadius: 6,
    overflow: 'hidden',           // clip header strip to the rounded corners
    transition: 'transform 0.08s, box-shadow 0.08s',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  },
  reportHeader: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 14px',
    fontSize: 13, fontWeight: 700,
    letterSpacing: 0.3,
  },
  reportTitle: { fontSize: 13, fontWeight: 700 },
  reportBodyInner: { padding: '12px 14px' },
  reportSummary: { fontSize: 13, color: '#2d3436', lineHeight: 1.5, marginBottom: 8 },
  reportFooter: {
    fontSize: 12, color: '#636e72', display: 'flex',
    alignItems: 'center', gap: 6, flexWrap: 'wrap',
  },
  downloadLink: { color: '#0984e3', fontWeight: 600 },
  reportMeta: { color: '#636e72' },
  legend: {
    fontSize: 12, color: '#636e72', textAlign: 'center',
    padding: '12px 0', borderTop: '1px solid #ecf0f1', marginTop: 16,
  },

  // Suggestion section — collapsed/expanded + grouped list.
  suggestionToggleClosed: {
    width: '100%', padding: '12px 16px', marginBottom: 16,
    background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 6,
    fontSize: 14, fontWeight: 600, color: '#475569',
    cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
    transition: 'border-color 0.15s, background 0.15s',
  },
  suggestionToggleHint: { color: '#94a3b8', fontWeight: 400, marginLeft: 6, fontSize: 12 },
  suggestionToggleOpen: {
    width: '100%', padding: '8px 16px', marginBottom: 12,
    background: '#fafbfc', border: '1px solid #e2e8f0', borderRadius: 6,
    fontSize: 14, fontWeight: 600, color: '#475569',
    cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
  },
  suggestionBlockOpen: {
    background: '#fff', borderRadius: 8, padding: 16, marginBottom: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    border: '1px solid #e2e8f0',
  },
  suggestionTextarea: {
    width: '100%', minHeight: 60, padding: '8px 10px',
    borderRadius: 6, border: '1px solid #dfe6e9', fontFamily: 'inherit',
    fontSize: 13, resize: 'vertical', boxSizing: 'border-box',
  },
  suggestionRow: {
    display: 'flex', alignItems: 'center', gap: 10, marginTop: 8,
  },
  charCount: { fontSize: 11, color: '#95a5a6' },
  toast: { fontSize: 12, color: '#00b894', fontWeight: 600 },
  submitBtn: {
    padding: '6px 14px', borderRadius: 6, border: 'none',
    background: '#475569', color: '#fff', fontWeight: 600, fontSize: 13,
    fontFamily: 'inherit',
  },
  suggestionListWrap: { marginTop: 16 },
  suggestionGroup: { marginBottom: 12 },
  suggestionGroupHeader: {
    fontSize: 11, fontWeight: 700, color: '#475569',
    letterSpacing: 0.8, textTransform: 'uppercase',
    marginTop: 12, marginBottom: 6, paddingBottom: 4,
    borderBottom: '1px solid #e2e8f0',
  },
  suggestionGroupCount: { fontWeight: 400, color: '#94a3b8', marginLeft: 4 },
  suggestionEmpty: { fontSize: 12, color: '#95a5a6', padding: 12 },
  suggestionItem: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px',
    borderBottom: '1px solid #f5f6fa', fontSize: 13, cursor: 'default',
  },
  suggestionPill: {
    display: 'inline-block', padding: '2px 8px', borderRadius: 3,
    fontSize: 10, fontWeight: 600, border: '1px solid', whiteSpace: 'nowrap',
    letterSpacing: 0.5, textTransform: 'uppercase',
  },
  suggestionText: { color: '#2d3436', flex: 1 },
  suggestionMeta: { fontSize: 11, color: '#95a5a6', whiteSpace: 'nowrap' },
  showDismissedBtn: {
    background: 'none', border: 'none', color: '#94a3b8',
    cursor: 'pointer', fontSize: 11, marginTop: 12, padding: 4,
    fontFamily: 'inherit', letterSpacing: 0.5, textTransform: 'uppercase',
    fontWeight: 600,
  },
  editBtn: {
    background: 'none', border: 'none', color: '#475569',
    cursor: 'pointer', fontSize: 11, padding: '2px 6px',
    fontFamily: 'inherit', whiteSpace: 'nowrap',
  },
  editTextarea: {
    width: '100%', padding: '6px 10px', borderRadius: 6,
    border: '1px solid #dfe6e9', fontFamily: 'inherit', fontSize: 13,
    resize: 'vertical', boxSizing: 'border-box',
  },
  editError: { fontSize: 11, color: '#d63031' },
  cancelBtn: {
    padding: '4px 12px', borderRadius: 4, border: '1px solid #dfe6e9',
    background: '#fff', cursor: 'pointer', fontSize: 12,
    fontFamily: 'inherit', color: '#636e72',
  },
  saveBtn: {
    padding: '4px 12px', borderRadius: 4, border: 'none',
    background: '#475569', color: '#fff', cursor: 'pointer',
    fontSize: 12, fontFamily: 'inherit', fontWeight: 600,
  },
};
